/**
 * /admin/messages — WhatsApp-Web-style admin tool (issue #351, subtasks 05–06).
 *
 * Left pane: union of every platform user (``/api/admin/users``) and
 * every existing chat thread (``/v1/chat/admin/threads/all``). Users
 * with an open thread surface a snippet + per-admin unread; users
 * without a thread render as "Noch keine Nachrichten" so the admin
 * can start a fresh dialog with anyone.
 *
 * Right pane: full thread view with reply footer. Reuses MessageBubble
 * from the user widget so soft-delete tombstones render identically
 * on both sides. Each bubble carries a ``⋯`` context menu (subtask 06)
 * with a destructive Delete action. Confirm opens a ConfirmationModal
 * showing the original body; on confirm, DELETE /chat/admin/messages/{id}
 * (#998 moved the URL under /chat/admin/ so BFF impersonation routing
 * catches the prefix) fans out a ``chat:message.deleted`` SSE event to
 * the user widget + any subscribed admins.
 *
 * Cross-tenant: each user is also their own tenant in the farmer model
 * (``backend/src/core/tenant.py``). We pass the selected user's id as
 * ``tenant_id`` to every admin mutation so RLS scopes into the right
 * tenant — the v1 same-tenant 403 was dropped on 2026-05-23.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, Trash2, X } from 'lucide-react';
import { ContextMenu, ConfirmationModal } from '@hofsync/design-system';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  chatKeys,
  useAdminChatMessages,
  useAdminChatThreadsAll,
  useAdminDeleteMessage,
  useAdminEnsureThread,
  useAdminMarkRead,
} from '../hooks/useChatQueries';
import { adminReply } from '../services/chatApi';
import { taggedFetch } from '../services/taggedFetch';
import { MessageBubble } from '../components/chat/MessageBubble';
import { withDateSplitters } from '../components/chat/DateSplitter';
import { useAutoGrowTextarea } from '../hooks/useAutoGrowTextarea';
import type { ChatMessage, AdminChatThread } from '../services/chatApi';
import './AdminMessagesPage.css';
import '../components/chat/MessageBubble.css';

interface AdminUser {
  id: string;
  phone: string;
  first_name: string | null;
  last_name: string | null;
  farm_name: string | null;
  last_active_at: string | null;
  account_type: string;
  inviter_id: string | null;
}

interface PanelRow {
  userId: string;
  // Effective tenant id — for employees this is ``inviter_id``, for
  // everyone else it's the user's own id. Used to pin every admin
  // mutation (reply / mark-read / delete / messages-fetch) so RLS
  // scopes into the right tenant.
  tenantId: string;
  displayName: string;
  thread: AdminChatThread | null;
  lastActiveAt: string | null;
}

function effectiveTenantOf(user: AdminUser): string {
  if (user.account_type === 'employee' && user.inviter_id) return user.inviter_id;
  return user.id;
}

function relativeTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString();
}

function displayNameFor(user: AdminUser): string {
  const first = user.first_name?.trim();
  const last = user.last_name?.trim();
  if (first || last) return [first, last].filter(Boolean).join(' ');
  if (user.farm_name?.trim()) return user.farm_name.trim();
  if (user.phone) return user.phone;
  return user.id;
}

const AdminMessagesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const threadsQuery = useAdminChatThreadsAll();
  const threads = threadsQuery.data ?? [];

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await taggedFetch('/api/admin/users?limit=500', { credentials: 'include' });
        if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
        const data = (await res.json()) as AdminUser[];
        if (!cancelled) setUsers(data);
      } catch (err) {
        if (!cancelled) setUsersError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const panelRows: PanelRow[] = useMemo(() => {
    // Canonical chat threads have ``tenant_id == user_id`` (the tenant
    // owner is also the thread's user_id). Multiple platform users can
    // share one tenant (farmer + employees), so we match by tenant —
    // every user in that tenant points to the same thread.
    const threadByTenant = new Map<string, AdminChatThread>();
    threads.forEach((row) => {
      const tenant = String(row.tenant_id ?? row.user_id);
      if (tenant) threadByTenant.set(tenant, row);
    });

    const merged = new Map<string, PanelRow>();
    users.forEach((u) => {
      const tenant = effectiveTenantOf(u);
      merged.set(u.id, {
        userId: u.id,
        tenantId: tenant,
        displayName: displayNameFor(u),
        thread: threadByTenant.get(tenant) ?? null,
        lastActiveAt: u.last_active_at,
      });
    });
    threads.forEach((thread) => {
      const tenant = String(thread.tenant_id);
      // Synthesize a row for a thread whose tenant isn't in /api/admin/users
      // (deleted user, unauthenticated employee, etc.) so the admin
      // doesn't lose access to historical conversations.
      if (!tenant) return;
      const alreadyCovered = Array.from(merged.values()).some(
        (r) => r.tenantId === tenant,
      );
      if (alreadyCovered) return;
      merged.set(tenant, {
        userId: tenant,
        tenantId: tenant,
        displayName: tenant,
        thread,
        lastActiveAt: thread.last_message_at,
      });
    });

    return Array.from(merged.values()).sort((a, b) => {
      if (a.thread && !b.thread) return -1;
      if (!a.thread && b.thread) return 1;
      if (a.thread && b.thread) {
        return (
          new Date(b.thread.last_message_at).getTime() -
          new Date(a.thread.last_message_at).getTime()
        );
      }
      return a.displayName.localeCompare(b.displayName);
    });
  }, [threads, users]);

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const selectedRow = useMemo(
    () => panelRows.find((row) => row.userId === selectedUserId) ?? null,
    [panelRows, selectedUserId],
  );

  // The "selected thread" can be (a) an existing thread from the
  // aggregated feed or (b) a freshly-ensured one after the admin
  // clicks a user with no prior conversation. Keyed by tenantId
  // because the canonical thread is per-tenant, not per-user.
  const [ensuredThreadByTenant, setEnsuredThreadByTenant] = useState<Record<string, string>>({});
  const selectedTenantId = selectedRow?.tenantId ?? undefined;
  const selectedThreadId =
    selectedRow?.thread?.thread_id ??
    (selectedTenantId ? ensuredThreadByTenant[selectedTenantId] : undefined) ??
    undefined;

  const qc = useQueryClient();
  const ensureThread = useAdminEnsureThread();

  const messagesQuery = useAdminChatMessages(selectedThreadId, selectedTenantId);
  const del = useAdminDeleteMessage(selectedThreadId, selectedTenantId);
  const markRead = useAdminMarkRead(selectedThreadId, selectedTenantId);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ChatMessage | null>(null);
  const textareaRef = useAutoGrowTextarea(draft, { minRows: 1, maxRows: 5 });

  const messagesScrollRef = useRef<HTMLDivElement | null>(null);
  const bubbleRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const debounceRef = useRef<number | null>(null);
  const lastReadRef = useRef<string | null>(null);
  const READ_DEBOUNCE_MS = 250;

  const messagesOldestFirst: ChatMessage[] = useMemo(() => {
    if (!messagesQuery.data) return [];
    return [...messagesQuery.data].sort(
      (a, b) => new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime(),
    );
  }, [messagesQuery.data]);

  const scheduleMarkRead = useCallback(
    (messageId: string) => {
      if (!selectedThreadId) return;
      if (lastReadRef.current === messageId) return;
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
      }
      debounceRef.current = window.setTimeout(() => {
        lastReadRef.current = messageId;
        markRead.mutate(messageId);
        debounceRef.current = null;
      }, READ_DEBOUNCE_MS);
    },
    [markRead, selectedThreadId],
  );

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const container = messagesScrollRef.current;
    if (!container) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        let bestMessageId: string | null = null;
        let bestSentAt = 0;
        for (const entry of entries) {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue;
          const el = entry.target as HTMLElement;
          const id = el.dataset.messageId;
          const sentAt = Number(el.dataset.sentAtMs ?? 0);
          if (id && sentAt >= bestSentAt) {
            bestMessageId = id;
            bestSentAt = sentAt;
          }
        }
        if (bestMessageId) scheduleMarkRead(bestMessageId);
      },
      { root: container, threshold: [0.5] },
    );
    for (const node of bubbleRefs.current.values()) {
      observerRef.current.observe(node);
    }
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [selectedThreadId, scheduleMarkRead, messagesOldestFirst]);

  useEffect(() => {
    lastReadRef.current = null;
  }, [selectedThreadId]);

  const registerBubble = useCallback(
    (msg: ChatMessage, node: HTMLDivElement | null) => {
      const map = bubbleRefs.current;
      if (node === null) {
        map.delete(msg.message_id);
        return;
      }
      node.dataset.messageId = msg.message_id;
      node.dataset.sentAtMs = String(new Date(msg.sent_at).getTime());
      map.set(msg.message_id, node);
      observerRef.current?.observe(node);
    },
    [],
  );

  const handleSend = async () => {
    const trimmed = draft.trim();
    if (!trimmed || !selectedUserId || !selectedTenantId) return;
    setSending(true);
    try {
      let threadId = selectedThreadId;
      if (!threadId) {
        // Backend resolves the user's effective tenant (employee →
        // inviter) when ensuring the thread.
        const ensured = await ensureThread.mutateAsync({ userId: selectedUserId });
        threadId = ensured.thread_id;
        setEnsuredThreadByTenant((prev) => ({
          ...prev,
          [selectedTenantId]: ensured.thread_id,
        }));
      }
      await adminReply(threadId as string, trimmed, selectedTenantId);
      setDraft('');
      qc.invalidateQueries({ queryKey: chatKeys.threadMessages(threadId as string) });
      qc.invalidateQueries({ queryKey: chatKeys.adminThreadsAll() });
    } catch (err) {
      // No-op: error toasts are out of scope for v1.
    } finally {
      setSending(false);
    }
  };

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    del.mutate(pendingDelete.message_id, {
      onSettled: () => setPendingDelete(null),
    });
  };

  // Resolve a chat author_id (typically the user's id in user-posted
  // messages) to the platform display name from /api/admin/users so
  // the bubble label reads "Erika Schuster" instead of a raw UUID.
  const displayNameByUserId = useMemo(() => {
    const map = new Map<string, string>();
    users.forEach((u) => map.set(u.id, displayNameFor(u)));
    return map;
  }, [users]);

  const renderBubbleWithMenu = (msg: ChatMessage) => {
    const isDeleted = Boolean(msg.deleted_at);
    const authorLabel =
      msg.author_kind === 'user'
        ? (displayNameByUserId.get(msg.author_id) ?? msg.author_id)
        : undefined;
    return (
      <div
        key={msg.message_id}
        ref={(node) => registerBubble(msg, node)}
        className="admin-messages__bubble-wrap"
        data-author-kind={msg.author_kind}
        data-testid="admin-bubble-wrap"
      >
        <MessageBubble message={msg} viewerKind="admin" authorLabel={authorLabel} />
        {!isDeleted && (
          <div className="admin-messages__bubble-menu">
            <ContextMenu
              ariaLabel={t('common.openMenu', 'Open menu')}
              items={[
                {
                  id: 'delete',
                  label: t('common.delete', 'Delete'),
                  icon: Trash2,
                  danger: true,
                  onClick: () => setPendingDelete(msg),
                },
              ]}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="admin-messages">
      <aside className="admin-messages__sidebar" aria-label={t('chat.admin.messagesTitle', 'User Messages')}>
        <header className="admin-messages__sidebar-header">
          <h2>{t('chat.admin.messagesTitle', 'User Messages')}</h2>
          <button
            type="button"
            className="admin-messages__exit"
            aria-label={t('common.close', 'Close')}
            title={t('chat.admin.closeTool', 'Close messaging tool')}
            onClick={() => navigate('/admin')}
          >
            <X size={20} aria-hidden />
          </button>
        </header>
        <ul className="admin-messages__thread-list">
          {usersLoading && (
            <li className="admin-messages__empty">
              {t('common.loading', 'Loading…')}
            </li>
          )}
          {usersError && !usersLoading && (
            <li className="admin-messages__empty">{usersError}</li>
          )}
          {!usersLoading && !usersError && panelRows.length === 0 && (
            <li className="admin-messages__empty">{t('chat.admin.noUsers', 'No users yet.')}</li>
          )}
          {panelRows.map((row) => {
            const isActive = row.userId === selectedUserId;
            const snippet = row.thread
              ? (row.thread.last_body ?? '').slice(0, 60)
              : t('chat.admin.noMessagesYet', 'No messages yet');
            const timeLabel = row.thread
              ? relativeTime(row.thread.last_sent_at)
              : '';
            const unread = row.thread?.unread_count ?? 0;
            return (
              <li key={row.userId}>
                <button
                  type="button"
                  className={`admin-messages__thread${isActive ? ' admin-messages__thread--active' : ''}`}
                  onClick={() => setSelectedUserId(row.userId)}
                  data-testid="admin-thread-row"
                >
                  <div className="admin-messages__thread-name">{row.displayName}</div>
                  <div className="admin-messages__thread-snippet">{snippet}</div>
                  <div className="admin-messages__thread-meta">
                    <span>{timeLabel}</span>
                    {unread > 0 && (
                      <span className="admin-messages__unread-badge" data-testid="admin-unread">
                        {unread}
                      </span>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </aside>
      <section className="admin-messages__thread-view" aria-live="polite">
        {!selectedRow ? (
          <p className="admin-messages__empty">
            {t('chat.admin.noThreadSelected', 'Select a user on the left to view the conversation.')}
          </p>
        ) : (
          <>
            <header className="admin-messages__thread-header">
              <h3>{selectedRow.displayName}</h3>
              <span className="admin-messages__thread-tenant">{selectedRow.tenantId}</span>
            </header>
            <div
              ref={messagesScrollRef}
              className="admin-messages__messages"
              data-testid="admin-messages"
            >
              {messagesOldestFirst.length === 0 && (
                <p className="admin-messages__empty">
                  {t('chat.admin.startConversation', 'No messages yet. Send the first reply to start the conversation.')}
                </p>
              )}
              {withDateSplitters(messagesOldestFirst, renderBubbleWithMenu)}
            </div>
            <form
              className="admin-messages__composer"
              onSubmit={(e) => {
                e.preventDefault();
                handleSend();
              }}
            >
              <div className="admin-messages__input-wrap">
                <textarea
                  ref={textareaRef}
                  className="admin-messages__input"
                  placeholder={t('chat.admin.replyPlaceholder', 'Type a reply…')}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  rows={1}
                />
                <button
                  type="submit"
                  className="admin-messages__send"
                  aria-label={t('chat.input.send', 'Send')}
                  title={t('chat.input.send', 'Send')}
                  disabled={sending || !draft.trim()}
                >
                  <Send size={18} />
                </button>
              </div>
            </form>
          </>
        )}
      </section>
      <ConfirmationModal
        isOpen={pendingDelete !== null}
        title={t('chat.admin.deleteConfirm.title', 'Delete message?')}
        message={
          pendingDelete?.body ??
          t('chat.admin.deleteConfirm.body', 'This will hide the message from the user. The original text is preserved in the audit log.')
        }
        confirmLabel={t('chat.admin.deleteConfirm.confirmButton', 'Delete')}
        cancelLabel={t('common.cancel', 'Cancel')}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
        danger
        loading={del.isPending}
      />
    </div>
  );
};

export default AdminMessagesPage;
